import React from "react";
import { Message } from "components/Atoms/Message";
import MakePaymentModal from "./MakePaymentModal";
import { DeleteDialog, Modal } from "../../components";
import PaymentsTable from "./PaymentsTable";
import PanelSegmentType from "components/panels/PanelSegment/PanelSegment";
import { useCustomQuery } from "utilities/hookutils";
import { MainPanelOrSectionPanel } from "components/panels";
import { isInvoiceOverdue } from "utilities/invoiceUtils";
import { useShortcutPanel } from "components/Shortcuts/hooks";

interface Props {
  invoice: server.dto.InvoiceV2 | server.dto.InvoiceV1
  canModify: boolean
  useSectionPanel?: boolean
  onRefetchedPayments?: () => void;
}

export const getPaymentsByInvoiceQueryKey = (invoiceId: number) => ["payments", invoiceId];

export const usePaymentsByInvoiceQuery = (invoiceId: number) => {
  return useCustomQuery({
    queryKey: getPaymentsByInvoiceQueryKey(invoiceId),
    queryFn: async () => window.CoreApi.Payment.ListByInvoice({ invoiceId: invoiceId }).then(r => r.data),
  });
}

const PaymentsPanel = (props: Props) => {
  const { canModify, useSectionPanel } = props;

  const [editingPayment, setEditingPayment] = React.useState<server.dto.Payment | null>(undefined);
  const [deletingPayment, setDeletingPayment] = React.useState<server.dto.Payment>(undefined);

  const onNewPaymentClicked = () => {
    setEditingPayment(null);
  };

  const isInvoiceV2 = (invoice: server.dto.InvoiceV2 | server.dto.InvoiceV1): invoice is server.dto.InvoiceV2 => {
    return "textCreditNotes" in invoice;
  };

  const reloadIfNeeded = (payment: server.dto.PaymentsForInvoice) => {
    if (!isInvoiceV2(props.invoice)) return;

    if (props.invoice.lifeCycle === "pending" && payment.outstanding.amount === 0) {
      window.location.reload();
    } else if (props.invoice.lifeCycle === "completed" && payment.outstanding.amount !== 0) {
      window.location.reload();
    }
  };

  const onPaymentSaved = async () => {
    setEditingPayment(undefined);
    const newResult = await paymentsApi.refetch();
    props.onRefetchedPayments?.();

    reloadIfNeeded(newResult.data.data);
  };

  const deletePayment = async (payment: server.dto.Payment) => {
    await window.CoreApi.Payment.Delete({ paymentId: payment.paymentId });
    setDeletingPayment(undefined);
    const res = await paymentsApi.refetch();
    props.onRefetchedPayments?.();
    reloadIfNeeded(res.data.data);
    return res;
  };

  const paymentsApi = usePaymentsByInvoiceQuery(props.invoice.invoiceId);

  const isLoading = paymentsApi.isInitialLoading && !paymentsApi.data;
  const paymentsData = paymentsApi.data?.data;
  const isSettled = paymentsData?.outstanding.amount === 0;
  const hasPayments = (paymentsData?.collection.length ?? 0) > 0;

  const { refToScrollTo } = useShortcutPanel({
    type: "payment",
    label: RESX.Payment.Payments,
    totalItems: hasPayments ? paymentsData.collection.length : 0,
    onAddClicked: canModify ? onNewPaymentClicked : undefined
  });

  return <MainPanelOrSectionPanel
    panelRef={refToScrollTo}
    useSectionPanel={useSectionPanel}
    title={RESX.Payment.Payments}
    action={canModify && <PanelSegmentType.Button onClick={onNewPaymentClicked} label={RESX.Buttons.btnNew} type="secondary" />}
    isLoading={isLoading}
    legacyPanelProps={{
      panelClassName: "s1_related-feature-panel"
    }}
  >
    {!isLoading && hasPayments && paymentsData && <PaymentsTable
      isOverdue={isInvoiceOverdue(props.invoice.invoiceDate, props.invoice.dueInDays, props.invoice.lifeCycle)}
      payments={paymentsData}
      onEdit={setEditingPayment}
      onDelete={setDeletingPayment}
      canModify={canModify}
    />}

    {!isLoading && !hasPayments && <Message className="flex" type={isSettled ? "info" : "error"}>
      <div className="grow1">
        {RESX.Financial.Outstanding}
      </div>
      <div className="s1_cell_financialamount">
        {paymentsData.outstanding.formatted}
      </div>
    </Message>}

    <Modal
      isOpen={editingPayment !== undefined}
      handleClose={() => { setEditingPayment(undefined); }}
      title={editingPayment == null ? RESX.Payment.NewPayment : RESX.Payment.EditPayment}
      width={"small"}
    >
      <MakePaymentModal
        invoiceId={props.invoice.invoiceId}
        onSaved={onPaymentSaved}
        outstandingAmount={paymentsData?.outstanding.amount ?? 0}
        currency={props.invoice.currency}
        existingPayment={editingPayment}
      />
    </Modal>

    <DeleteDialog
      isOpen={deletingPayment != null}
      message={RESX.GeneralWarnings.DeleteSure}
      handleClose={() => { setDeletingPayment(undefined); }}
      onDelete={() => deletePayment(deletingPayment)}
    />
  </MainPanelOrSectionPanel>;
};

export default PaymentsPanel;